import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import SystemWelcome from "@/components/layout/SystemWelcome";
import AppErrorBoundary from "@/components/shared/AppErrorBoundary";

import { RuntimeProofGuard } from "@/utils/runtimeProofGuard";
import { renderCheckpoint } from "@/utils/renderCheckpoint";

// Page Imports
import Dashboard from "@/pages/Dashboard";
import Startups from "@/pages/Startups";
import FoundersList from './pages/crm/founders/index';
import FounderDetail from './pages/crm/founders/[id]';
import KnowledgeBase from './pages/KnowledgeBasePage';
import Investors from "@/pages/Investors";
import CeoOffice from '@/pages/CeoOffice';
import ToolBank from '@/pages/ToolBank';
import EmailOutreach from '@/pages/agents/EmailOutreach';
import ArticleAgent from '@/pages/agents/ArticleAgent';
import LinkedInPostAgent from '@/pages/agents/LinkedInPostAgent';
import DealEvaluatorAgent from '@/pages/agents/DealEvaluatorAgent';
import DealScreenDetail from '@/pages/agents/DealScreenDetail';
import DealSourcingAgent from '@/pages/agents/DealSourcingAgent';
import CrossSectorPatternAgent from '@/pages/agents/CrossSectorPatternAgent';
import PrincipleLibraryAgent from '@/pages/agents/PrincipleLibraryAgent';
import ComingSoon from '@/pages/ComingSoon';
import StrategyInsightsPage from '@/pages/StrategyInsightsPage';
import ComponentDemo from '@/pages/ComponentDemo';
import { UI_INVARIANTS } from "./config/ui-invariants";
import { ROUTES } from "./config/product-contract";
import "@/App.css";

// Phase 4: Lifecycle Lock Implementation
const ShellCheckpoint = ({ children }) => children;
const RouterCheckpoint = ({ children }) => children;

function App() {
  // DEBUG: Phase 20 - Render Isolation Bypass
  // Change to true if white screen persists after race fix
  const DEBUG_ISOLATION = false;
  if (DEBUG_ISOLATION) return <div data-proof="RENDER_REACHED">Render reached</div>;

  const hasWiped = React.useRef(false);
  if (!hasWiped.current) {
    UI_INVARIANTS.SESSION.wipeStaleState();
    hasWiped.current = true;
  }
  const [isLifecycleComplete, setIsLifecycleComplete] = React.useState(UI_INVARIANTS.SESSION.getIsLifecycleComplete());

  // FIX: Listen for external lifecycle completion signal (from SystemWelcome)
  useEffect(() => {
    console.log('[DEBUG_LIFECYCLE] App: Mount');

    // RACE FIX: Check global state immediately in case we missed the event
    if (UI_INVARIANTS.SESSION.getIsLifecycleComplete()) {
      console.log('[DEBUG_LIFECYCLE] App: Lifecycle Complete (Global State Check)');
      setIsLifecycleComplete(true);
    }

    const handleLifecycleChange = () => {
      console.log('[DEBUG_LIFECYCLE] App: Lifecycle Signal Received via Event.');
      setIsLifecycleComplete(true);
    };

    window.addEventListener('ACCREZEO_LIFECYCLE_CHANGED', handleLifecycleChange);
    return () => window.removeEventListener('ACCREZEO_LIFECYCLE_CHANGED', handleLifecycleChange);
  }, []);

  useEffect(() => {
    // Phase 6: Sync Phase with Lifecycle
    if (isLifecycleComplete && UI_INVARIANTS.STATE.getPhase() === UI_INVARIANTS.PHASES.PRE_RENDER) {
      UI_INVARIANTS.STATE.setPhase(UI_INVARIANTS.PHASES.RENDER);
    }
  }, [isLifecycleComplete]);

  // Phase 3: Sequence Law Enforcement
  // If we are in RENDER phase but lifecycle isn't complete (paradox), throw.
  if (isLifecycleComplete === false && UI_INVARIANTS.STATE.getPhase() === UI_INVARIANTS.PHASES.RENDER) {
    const error = 'LIFECYCLE_ORDER_VIOLATION: Modules active before Welcome completion.';
    console.error(error);
    throw new Error(error);
  }

  console.log('[OS_BOOT] Shell Rendering. Phase:', UI_INVARIANTS.STATE.getPhase(), 'Lifecycle:', isLifecycleComplete);

  // Phase 5: Routing Authority - Baseline is "/"

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        {/* Stage 1: Absolute Enrollment / Welcome */}
        {!isLifecycleComplete && <SystemWelcome />}

        {/* Stage 2: Main Application Shell (Locked until Stage 1 resolves) */}
        {isLifecycleComplete && (
          <Routes>
            <Route element={<AppLayout><Outlet /></AppLayout>}>
              <Route path={ROUTES.ROOT} element={<Navigate to={ROUTES.CEO_OFFICE} replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/component-demo" element={<ComponentDemo />} />

              {/* CEO Office */}
              <Route path={ROUTES.CEO_OFFICE} element={<CeoOffice />} />
              <Route path={ROUTES.STRATEGY_INSIGHTS} element={<StrategyInsightsPage />} />

              {/* Agents */}
              <Route path={ROUTES.LINKEDIN_SCOUT} element={<ComingSoon moduleName="LinkedIn Scout" />} />
              <Route path={ROUTES.DEAL_SOURCING} element={<DealSourcingAgent />} />
              <Route path={ROUTES.SECTOR_INTELLIGENCE} element={<ArticleAgent />} />
              <Route path={ROUTES.FOUNDER_OUTREACH} element={<EmailOutreach />} />
              <Route path={ROUTES.LINKEDIN_POST} element={<LinkedInPostAgent />} />
              <Route path={ROUTES.DEAL_EVALUATOR} element={<DealEvaluatorAgent />} />
              <Route path={ROUTES.DEAL_EVALUATOR_DETAIL} element={<DealScreenDetail />} />
              <Route path={ROUTES.CROSS_SECTOR_PATTERNS} element={<CrossSectorPatternAgent />} />
              <Route path={ROUTES.PRINCIPLE_LIBRARY} element={<PrincipleLibraryAgent />} />

              {/* Data */}
              <Route path={ROUTES.STARTUPS} element={<Startups />} />
              <Route path={ROUTES.FOUNDERS} element={<FoundersList />} />
              <Route path={ROUTES.FOUNDER_DETAIL} element={<FounderDetail />} />
              <Route path={ROUTES.INVESTORS} element={<Investors />} />
              <Route path={ROUTES.KNOWLEDGE_BASE} element={<KnowledgeBase />} />
              <Route path={ROUTES.CAMPAIGNS} element={<ComingSoon moduleName="Campaigns" />} />
              <Route path={ROUTES.DEALS} element={<ComingSoon moduleName="Deals" />} />
              {/* The /ideas route and its IdeaQueue page were REMOVED on 2026-07-30
                  (WI-IDEA-CLAIM-REPAIR-AND-ONE-DOOR-AGENT-PAGE-001). The idea surface is now
                  the first tab of Post Studio at ROUTES.LINKEDIN_POST — one route, one
                  component, one entry. */}
              <Route path={ROUTES.TAGS} element={<ComingSoon moduleName="Tags" />} />

              {/* Legacy redirects */}
              <Route path="/inbox" element={<Navigate to={ROUTES.CEO_OFFICE} replace />} />
              <Route path="/agents/article-agent" element={<Navigate to={ROUTES.SECTOR_INTELLIGENCE} replace />} />
              <Route path="/agents/email-outreach" element={<Navigate to={ROUTES.FOUNDER_OUTREACH} replace />} />
              <Route path="/review" element={<Navigate to={ROUTES.CEO_OFFICE} replace />} />
              <Route path="/agents" element={<Navigate to={ROUTES.ROOT} replace />} />

              <Route path={ROUTES.SETTINGS} element={<div>Settings coming soon...</div>} />
              <Route path="*" element={<Navigate to={ROUTES.ROOT} replace />} />
            </Route>
          </Routes>
        )}
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

// Helper for nested routes
export default App;
